import type { PropsWithChildren } from 'react'
import type { AllCMSPageFields } from '@/cms/fields/mappers/common'
import type {
  AppPageProps,
  DocumentPageProps,
  RenderDocumentProps,
  RenderingStrategy,
  SlugPageProps,
} from '@/cms/rendering-strategy/types'
import NextHead from 'next/head'
import * as React from 'react'
import { useEffect } from 'react'
import { createIntl } from 'react-intl'
import { getFonts } from '@/framework/react-styles'
import StyledComponentsRegistry from '@/framework/styled-components/styled-components-registry'
import { getBrandName } from '@/helpers/environment'
import customIntlOnError from '@/intl/customIntlOnError'
import { reactPageRegistry as defaultPageRegistry } from '@/react-pages'
import { CeModalWrapper } from '@/scopes/account/sunweb-ce/components/CeModal'
import { createCeLoginApiClient } from '@/scopes/account/sunweb-ce/infrastructure/ceLoginApiClient'
import CookieBotScript from '@/scopes/common/components/Scripts/CookieBotScript'
import { SetABNewRelicCustomAttribute } from '@/scopes/common/components/Scripts/setABNewRelicCustomAttribute'
import TaggrsHeadScripts from '@/scopes/common/components/Scripts/TaggrsHeadScripts'
import { ThirdPartyScripts } from '@/scopes/common/components/Scripts/ThirdPartyScripts'
import { Configuration } from '@/scopes/common/domain/types/configuration'
import actions from '@/scopes/common/domain/use-cases/useCases'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import ga4CommonAnalyticsClient from '@/scopes/common/infrastructure/analytics/ga4CommonAnalyticsClient'
import cookieClient from '@/scopes/common/infrastructure/cookies/cookieClient'
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient'
import {
  createCmsMetaDescriptionResolver,
} from '@/scopes/common/infrastructure/meta-data/cmsMetaDescriptionResolver'
import {
  createCmsMetaTitleResolver,
} from '@/scopes/common/infrastructure/meta-data/cmsMetaTitleResolver'
import { createQueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'

// This type is skipping 'fields' from pageProps to prevent using fields that are not mapped
// TODO remove this and 'fields' from pageProps when the mapping is done for all fields and the unmapped fields are removed
interface FullReactRenderDocumentPageProps extends Omit<RenderDocumentProps, 'pageProps'> {
  pageProps: Omit<DocumentPageProps, 'fields'>
}

export function fullReactRenderingStrategy(): RenderingStrategy {
  function renderDocument({ Head, Html, Main, NextScript, pageProps }: FullReactRenderDocumentPageProps): React.JSX.Element {
    const page = pageProps?.page
    const addThirdPartyScripts = process.env.NEXT_PUBLIC_DISABLE_THIRD_PARTIES !== 'true'
    const addTaggrsScript
      = addThirdPartyScripts && featureFlagClient.isActive('KYOTO_USE_TAGGRS_SCRIPT')

    const { name, template, fields } = page || {}
    const { bodyAttrs, htmlLang } = fields || {}
    const { machineName } = bodyAttrs || {}
    const { config } = pageProps
    const baseAssetsImgUrl = `${config.domainUrl}/DesignSystem/WebAssets/${config.brandName}/img`

    // TODO use brand name from config instead
    const brandName = getBrandName()
    const { primaryFont, secondaryFont } = getFonts(brandName)

    return (
      <Html lang={htmlLang}>
        <Head>
          {addTaggrsScript && (
            <TaggrsHeadScripts
              id={config.thirdParties.taggrsId}
              url={config.thirdParties.taggrsUrl}
            />
          )}
          {addThirdPartyScripts && <CookieBotScript cookieBotId={config.thirdParties.cookieBotId} />}
          <link rel="shortcut icon" type="image/x-icon" href={`${baseAssetsImgUrl}/favicon.ico`} />
          <link rel="apple-touch-icon" sizes="180x180" href={`${baseAssetsImgUrl}/apple-touch-icon.png`} />
          <link rel="icon" type="image/png" sizes="32x32" href={`${baseAssetsImgUrl}/favicon-32x32.png`} />
          <link rel="icon" type="image/png" sizes="16x16" href={`${baseAssetsImgUrl}/favicon-16x16.png`} />
          <meta httpEquiv="x-ua-compatible" content="ie=edge" />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  "name": "${config.brandName}",
                  "url": "${config.domainUrl}",
                  "logo": "${config.domainUrl}/-/media/sundio/sunwebsplashpage/logo/sunweb-main-logo.ashx"
                }
            `,
            }}
          />

          {/*
              TODO missing tags:
              - CanonicalTagLinkOverride for every page
              - Generic script
              - Missing data structure for SEO
            */}
        </Head>
        <body
          data-qa-full-react="true"
          data-in-name={machineName}
          data-qa-page={name}
          data-qa-template={template}
          className={`${primaryFont.variable} ${secondaryFont.variable}`}
        >
          {addTaggrsScript && (
            <noscript>
              <iframe title="taggrsScript" src={`${config.thirdParties.taggrsUrl}/${config.thirdParties.taggrsId}.html`} height="0" width="0" style={{ display: 'none', visibility: 'hidden' }}></iframe>
            </noscript>
          )}
          <StyledComponentsRegistry>
            <Main />
            <NextScript />
          </StyledComponentsRegistry>
        </body>
      </Html>
    )
  }

  function renderHrefLangLinks(links: { language: string, url: string }[] = []) {
    return links.map(link => (
      <link key={link.language} rel="alternate" hrefLang={link.language} href={link.url} />
    ))
  }

  function renderApp({ Component, ReactPage, pageProps }: AppPageProps) {
    const locale = pageProps.config.locale
    const abTest = {
      isEnabled: Boolean(pageProps.enableAbTest),
      isBVariant: Boolean(pageProps.isAbTestBVariant),
    }
    const intl = createIntl({ locale, messages: pageProps.translations, onError: customIntlOnError })
    const { config, cmsFields } = pageProps
    const allCmsFields = cmsFields as AllCMSPageFields
    const defaultPageTitleResolver = createCmsMetaTitleResolver({ config, cmsFields: allCmsFields, intl })
    const defaultMetaDescriptionResolver = createCmsMetaDescriptionResolver({ config, cmsFields: allCmsFields, intl })
    const pageTitle = cmsFields?.metaTitle || defaultPageTitleResolver.resolve()
    const metaDescription = cmsFields?.metaDescription || defaultMetaDescriptionResolver.resolve()
    const canonicalUrl = `${config.domainUrl}${cmsFields?.canonicalUrl}`

    return (
      <ReactPage
        intl={intl}
        locale={locale}
        // TODO: This casting should be removed. In order to do so we need to remove exceptions for pages not using Uniform data
        cmsFields={allCmsFields}
        config={pageProps.config}
        tokens={pageProps.tokens}
        abTest={abTest}
      >
        {!(process.env.NEXT_PUBLIC_DISABLE_THIRD_PARTIES === 'true') && <ThirdPartyScripts configuration={config.thirdParties} />}
        {/* Metadata that may change/be overridden by page */}
        <NextHead>
          {pageTitle && <title>{pageTitle}</title>}
          {Boolean(metaDescription) && (
            <meta name="description" content={metaDescription} />
          )}
          <meta
            name="robots"
            content={`${cmsFields?.metaRobotsIndex ? 'index' : 'noindex'},${
              cmsFields?.metaRobotsFollow ? 'follow' : 'nofollow'
            }`}
          />
          {renderHrefLangLinks(cmsFields?.alternateUrlsData)}
          <link rel="canonical" href={canonicalUrl} key="canonical" />
        </NextHead>
        {/* JSON-LD rendered directly — valid anywhere in the document per Google spec, avoids next/head DOM conflicts */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "${pageTitle}",
                "url": "${canonicalUrl}"
              }
            `,
          }}
        />
        <FullReactPage
          config={pageProps.config}
        >
          <SetABNewRelicCustomAttribute cookieEnabled={Boolean(pageProps?.enableAbTest)} />
          <Component {...pageProps} />
        </FullReactPage>
      </ReactPage>
    )
  }

  function renderPageComponent({
    reactPageRegistry = defaultPageRegistry,
    ...pageProps
  }: SlugPageProps): React.JSX.Element {
    const PageComponent = reactPageRegistry.getComponentFor(pageProps)

    return <PageComponent {...pageProps} />
  }

  return {
    type: 'fullReact',
    renderDocument,
    renderApp,
    renderPageComponent,
  }
}

function FullReactPage({
  config,
  children,
}: PropsWithChildren<{
  config: Configuration
}>) {
  const queryStringClient = createQueryStringClient()
  const [showCeModal, setShowCeModal] = React.useState(false)
  const loadPageUseCases = actions({
    analyticsClient: ga4analyticsClient,
    commonAnalyticsClient: ga4CommonAnalyticsClient,
    cookieClient,
    queryStringClient,
    config,
    apiClient: createCeLoginApiClient(),
  })

  useEffect(() => {
    (async () => {
      const didLogin = await loadPageUseCases.reactPageLoaded()
      if (didLogin) {
        setShowCeModal(true)
      }
    })()
  }, [])

  return (
    <>
      { showCeModal && <CeModalWrapper />}
      {children}
    </>
  )
}
